import { useState } from 'react';

export type PreviewSlot = {
  /** Empty once `exhausted`: the caller should stop rendering the `<img>` and fall
   *  back to something else (e.g. the live iframe). */
  src: string;
  onError: () => void;
  exhausted: boolean;
};

/**
 * Gates one Finish-step device's static screenshot: `url` is the best capture
 * {@link findThemeScreenshot} already resolved (language preference included), or
 * empty if the backend has none for this selection. Reports `exhausted` immediately
 * when there is no URL to try, or once the `<img>` itself reports a load failure
 * (call `onError` from its `onError`), so the caller can fall back to the live iframe.
 */
export function usePreviewSlot(url: string): PreviewSlot {
  // Keyed on the URL that failed rather than reset in an effect, so a fast error can't be
  // cleared by an effect that flushes after it (that left the spinner up for good). A new
  // URL (selection change, or the resolved screenshot itself changing) gets its own fresh
  // attempt: the stale failure is dropped during render.
  const [failedUrl, setFailedUrl] = useState('');
  if (failedUrl !== '' && failedUrl !== url) setFailedUrl('');

  const exhausted = !url || failedUrl === url;
  return { src: exhausted ? '' : url, onError: () => setFailedUrl(url), exhausted };
}
